(function storefrontBoundary(eligibleVariantIds, currentLicenseVersion, currentLicenseSha256) {
  "use strict";

  const VERSION_KEY = "Creator License version";
  const HASH_KEY = "_DSIV license SHA-256";
  const SAFE_NAVIGATION_PROTOCOLS = new Set(["http:", "https:"]);
  const eligibleVariants = new Set(eligibleVariantIds);
  const shopify = (window.Shopify = window.Shopify || {});
  const priorNavigate = typeof shopify.navigate === "function" ? shopify.navigate.bind(shopify) : null;
  const status = {
    configured: false,
    configurationBlocked: false,
    lastRepairCount: 0,
    lastError: "",
  };
  let repairInFlight = null;

  function normalizeVariantId(value) {
    const raw = String(value || "").trim();
    return raw.startsWith("gid://shopify/ProductVariant/")
      ? raw.slice("gid://shopify/ProductVariant/".length)
      : raw;
  }

  function isEligibleVariant(value) {
    return eligibleVariants.has(normalizeVariantId(value));
  }

  function withLicenseAttributes(attributes) {
    const preserved = Array.isArray(attributes)
      ? attributes.filter((attribute) => attribute && attribute.key !== VERSION_KEY && attribute.key !== HASH_KEY)
      : [];
    return [
      ...preserved,
      { key: VERSION_KEY, value: currentLicenseVersion },
      { key: HASH_KEY, value: currentLicenseSha256 },
    ];
  }

  function decorateUpdateCartPayload(payload) {
    if (!payload || !Array.isArray(payload.lines)) return 0;
    let decorated = 0;
    for (const line of payload.lines) {
      if (!line || Number(line.quantity) === 0 || !isEligibleVariant(line.merchandiseId)) continue;
      line.attributes = withLicenseAttributes(line.attributes);
      decorated += 1;
    }
    return decorated;
  }

  function hasCurrentLicense(properties) {
    return properties?.[VERSION_KEY] === currentLicenseVersion
      && properties?.[HASH_KEY] === currentLicenseSha256;
  }

  async function readAjaxCart() {
    const response = await window.fetch("/cart.js", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
    return response.json();
  }

  async function replaceLineProperties(item) {
    const properties = {
      ...(item.properties || {}),
      [VERSION_KEY]: currentLicenseVersion,
      [HASH_KEY]: currentLicenseSha256,
    };
    const response = await window.fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties }),
    });
    if (!response.ok) throw new Error(`Cart repair failed (${response.status})`);
    await response.json();
  }

  async function repairCart() {
    let repairedCount = 0;
    for (let attempt = 0; attempt <= 250; attempt += 1) {
      const cart = await readAjaxCart();
      const invalidItem = (cart.items || []).find(
        (item) => isEligibleVariant(item.variant_id) && !hasCurrentLicense(item.properties),
      );
      if (!invalidItem) {
        status.lastRepairCount = repairedCount;
        status.lastError = "";
        return { cart, repairedCount };
      }
      if (attempt === 250) throw new Error("Cart repair exceeded Shopify's 250-line limit");
      await replaceLineProperties(invalidItem);
      repairedCount += 1;
    }
    throw new Error("Cart repair did not converge");
  }

  function ensureCartLicenseAssociation() {
    if (!repairInFlight) {
      repairInFlight = repairCart()
        .catch((error) => {
          status.lastError = error instanceof Error ? error.message : String(error);
          throw error;
        })
        .finally(() => {
          repairInFlight = null;
        });
    }
    return repairInFlight;
  }

  function isCheckoutPath(pathname) {
    const parts = String(pathname || "").split("/").filter(Boolean);
    if (parts[0] === "checkout" || parts[0] === "checkouts") return true;
    return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(parts[0] || "")
      && (parts[1] === "checkout" || parts[1] === "checkouts");
  }

  function cartRecoveryUrl() {
    const routeRoot = typeof window.Shopify?.routes?.root === "string"
      ? window.Shopify.routes.root
      : "/";
    let url = new URL(`${routeRoot.replace(/\/?$/, "/")}cart`, window.location.origin);
    if (!SAFE_NAVIGATION_PROTOCOLS.has(url.protocol) || url.origin !== window.location.origin) {
      url = new URL("/cart", window.location.origin);
    }
    url.searchParams.set("dsiv_license_check", "failed");
    return url.href;
  }

  function defaultNavigate(destination) {
    if (priorNavigate) return priorNavigate(destination.href);
    window.location.assign(destination.href);
    return undefined;
  }

  function guardedNavigate(target) {
    let destination;
    try {
      destination = new URL(String(target), window.location.origin);
    } catch {
      status.lastError = "Blocked invalid navigation target";
      throw new Error(status.lastError);
    }
    if (!SAFE_NAVIGATION_PROTOCOLS.has(destination.protocol)) {
      status.lastError = `Blocked unsafe navigation protocol (${destination.protocol})`;
      throw new Error(status.lastError);
    }
    if (destination.origin !== window.location.origin || !isCheckoutPath(destination.pathname)) {
      return defaultNavigate(destination);
    }
    return ensureCartLicenseAssociation()
      .then(() => window.location.assign(destination.href))
      .catch(() => window.location.assign(cartRecoveryUrl()));
  }

  shopify.navigate = guardedNavigate;

  function configureUpdateCartAction() {
    const action = window.Shopify?.actions?.updateCart;
    if (!action || typeof action.configure !== "function") return false;
    const configured = action.configure({
      eventTarget: () => null,
      async handler(defaultHandler, payload) {
        decorateUpdateCartPayload(payload);
        return defaultHandler();
      },
    });
    status.configured = configured === true;
    status.configurationBlocked = configured !== true;
    return true;
  }

  function initialize() {
    let attempts = 0;
    const tryConfigure = () => {
      attempts += 1;
      if (!configureUpdateCartAction() && attempts < 100) {
        window.setTimeout(tryConfigure, 50);
      }
    };
    tryConfigure();

    document.addEventListener("shopify:cart:lines-update", (event) => {
      Promise.resolve(event.promise).then(ensureCartLicenseAssociation).catch(() => {});
    });

    if (/\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i.test(window.location.pathname)) {
      ensureCartLicenseAssociation()
        .then(({ repairedCount }) => {
          if (repairedCount > 0) window.location.reload();
        })
        .catch(() => {});
    }
  }

  window.DSIVLicenseCartBoundary = Object.freeze({
    licenseVersion: currentLicenseVersion,
    eligibleVariantCount: eligibleVariants.size,
    isEligibleVariant,
    decorateUpdateCartPayload,
    repair: ensureCartLicenseAssociation,
    status: () => ({ ...status }),
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})([
  "33648504766600",
  "33658590101640",
  "33658697908360",
  "33658761609352",
  "33658788249736",
  "33658850672776",
  "33658895040648",
  "33658941046920",
  "33659018051720",
  "33659055243400",
  "33659085127816",
  "33659120287880",
  "33659148828808",
  "33659161706632",
  "33659179335816",
  "33659193327752",
  "33659235532936",
  "33659257290888",
  "33659310899336",
  "33659489845384",
  "33659617968264",
  "33659850457224",
  "33659938603144",
  "33660743680136",
  "33660770156680",
  "33660788670600",
  "33660830449800",
  "33660842606728",
  "33660856303752",
  "33660870492296",
  "33660884549768",
  "33660902998152",
  "33660912140424",
  "33660937732232",
  "33660951134344",
  "33660960538760",
  "33660984885384",
  "33661000188040",
  "33661020831880",
  "33661057990792",
  "33661142270088",
  "33661167534216",
  "33661184049288",
  "33661224517768",
  "33661247553672",
  "33661283336328",
  "33661311090824",
  "33661371220104",
  "33661393567880",
  "33661410246792",
  "33661768007816",
  "33661778395272",
  "33661794844808",
  "33661803430024",
  "33661816340616",
  "33661835870344",
  "33661848879240",
  "33661874831496",
  "33661889773704",
  "33661911433352",
  "33661928571016",
  "33661938663560",
  "33661952524424",
  "33661994533000",
  "33662014423176",
  "33662035263624",
  "33662057283720",
  "33662093066376",
  "33662116364424",
  "33664243105928",
  "33664247857288",
  "33664259424392",
  "33664269123720",
  "33664279216264",
  "33664296517768",
  "33664321388680",
  "33664336625800",
  "33664341147784",
  "33680292610184",
  "39462765232264",
  "39462774440072",
  "39462789644424",
  "39462797738120",
  "39462804914312",
  "39462815170696",
  "39462820020360",
  "39462828310664",
  "39462925598856",
  "39462928318600",
  "39462932447368",
  "39462934741128",
  "39462939361416",
  "39462941393032",
  "39462947487880",
  "39462950142088",
  "39462952009864",
  "39462953812104",
  "39462956662920",
  "39462958629000",
  "39462960693384",
  "39462962790536",
  "39462965543048",
  "39462967640200",
  "39462970294408",
  "39462977503368",
  "39462978879624",
  "39462980190344",
  "39462981501064",
  "39462983205000",
  "39462984745096",
  "39462986842248",
  "39462988251272",
  "39462989496456",
  "39462993100936",
  "39462994575496",
  "39462995591304",
  "39462997688456",
  "39463000866952",
  "39463004831880",
  "39463006732424",
  "39463008567432",
  "39463010533512",
  "39463012368520",
  "39463016267912",
  "39463019708552",
  "42432161448072",
  "42432282755208",
  "42432313458824",
  "42432377618568",
  "42432422117512",
  "42432777453704",
  "42432812875912",
  "42432874152072",
  "42432940900488",
  "42432991690888",
  "42433092518024",
  "42433141407880",
  "42433265500296",
  "42433290928264",
  "42433313079432",
  "42433332215944",
  "42433369604232",
  "42433393787016",
  "42433422229640",
  "42435025272968",
  "42435056173192",
  "42435077701768",
  "42435097723016",
  "42435114369160",
  "42435142615176",
  "42435943923848",
  "42435947823240",
  "42435949199496",
  "42435952345224",
  "42435952869512",
  "42435953262728",
  "42439886078088",
  "42439886373000",
  "42439886700680",
  "42439887093896",
  "42439887224968",
  "42439887454344",
  "42439891779720",
  "42439894433928",
  "42439898333320",
  "42439898562696",
  "42439898890376",
  "42439899054216",
  "42439899218056",
  "42439900299400",
  "42439900463240",
  "42439900594312",
  "42439900823688",
  "42439903150216",
  "42439904657544",
  "42439904886920",
  "42439905542280",
  "42439906197640",
  "42439906492552",
  "42439908688008",
  "42440365473928",
  "42440368849032",
  "42440370716808",
  "42440372584584",
  "42440374386824",
  "42440377106568",
  "42440380940424",
  "42440392147080",
  "42440394080392",
  "42440398569608",
  "42440403714184",
  "42442459676808",
  "42442460266632",
  "42442461479048",
  "42442462724232",
  "42442464198792",
  "42442464493704",
  "42442465247368",
  "42442467672200",
  "42442468556936",
  "42442471145608",
  "42442472882312",
  "42442474750088",
  "42442475700360",
  "42442476945544",
  "42442478518408",
  "42442479960200",
  "42442487496840",
  "42442489593992",
  "42442490675336",
  "42442493034632",
  "42442493952136",
  "42442497196168",
  "42442497687688",
  "42442498343048",
  "42442499031176",
  "42442499358856",
  "42442499915912",
  "42442504011912",
  "42442504962184",
  "42442514432136",
  "42442516824200",
  "42442521018504",
  "42442523279496",
  "42442526425224",
  "42442530685064",
  "42442532487304",
  "42442534912136",
  "42442537271432",
  "42442539335816",
  "42442544447624",
  "42442547658888",
  "42442551165064",
  "42442554998920",
  "42442558374024",
  "42442560503944",
  "42442562437256",
  "42992636887176",
  "42992645472392",
  "42992654385288",
  "42992655958152",
  "42992659464328",
  "42992663298184",
  "42992681713800",
  "42992682434696",
  "42992685875336",
  "42992744202376",
  "42992746102920",
  "42992747708552",
  "42992750166152",
  "42992751902856",
  "42992752558216",
  "42992753672328",
  "42992754557064",
  "42992756293768",
  "42992757047432",
  "42992759472264",
  "42992760389768",
  "42992762552456",
  "42992763895944",
  "42992765763720",
  "42992768155784",
  "42992769597576",
  "42992772546696",
  "42992774545544",
  "42992778313864",
  "42992778608776",
  "42992779067528",
  "42992779296904",
  "42992780116104",
  "42992783163528",
  "42992783622280",
  "42992783949960",
  "42992784474248",
  "42992784769160",
  "42992785031304",
  "42992785457288",
  "42992785719432",
  "42992785817736",
  "42992786014344",
  "43214912815240",
  "43215265890440",
  "43215294398600",
  "43215319761032",
  "43215346466952",
  "43215357575304",
  "43215375237256",
  "43215393882248",
  "43215430877320",
  "43215449981064",
  "43215458500744",
  "43215460925576",
  "43215462301832",
  "43215464726664",
  "43215465709704",
  "43215467741320",
  "43215470100616",
  "43215471247496",
  "43215473180808",
  "43215476195464",
  "43215479865480",
  "43215481929864",
  "43215522955400",
  "43215530262664",
  "43215531180168",
  "43215531933832",
  "43215534719112",
  "43215538094216",
  "43215540617352",
  "43215552151688",
  "43215553364104",
  "43215553986696",
  "43215555002504",
  "43215555756168",
  "43215556247688",
  "43215556739208",
  "43215558574216",
  "43215560343688",
  "43215562244232",
  "43215563194504",
  "43215563522184",
  "43215564406920",
  "43215564767368",
  "43215565389960",
  "43215569780872",
  "43215571288200",
  "43787939709064",
  "43787945377928",
  "43787948032136",
  "43787950391432",
  "43787951145096",
  "43787953111176",
  "43787959238792",
  "43787982733448",
  "43787986927752",
  "43787990663304",
  "43787992891528",
  "43787994464392",
  "43787995578504",
  "43787996594312",
  "43788000100488",
  "43788000624776",
  "43788000919688",
  "43788002951304",
  "43788005408904",
  "43788005802120",
  "43788008554632",
  "43788011143304",
  "43788012814472",
  "43788013994120",
  "43788014289032",
  "43788016189576",
  "43788022087816",
  "43788022546568",
  "43788023038088",
  "43788030869640",
  "43788035948680",
  "43788036571272",
  "43788037619848",
  "43788038832264",
  "43788042666120",
  "43788046205064",
  "43788049252488",
  "43788049973384",
  "43788050923656",
  "43788052791432",
  "45825032126600",
  "45825035862152",
  "45825036025992",
  "45825036157064",
  "45825036288136",
  "45825036484744",
  "45825043497096",
  "45825043890312",
  "45825043988616",
  "45825044021384",
  "45825044185224",
  "45825044742280",
  "45825044840584",
  "45825044938888",
  "46252581224584",
  "46252581290120",
  "46252581322888",
  "46252581355656",
  "46252581388424",
  "46252581421192",
  "46252581453960",
  "46252581683336",
  "46252581716104",
  "46252581748872",
  "46252581814408",
  "46252581978248",
  "46252582011016",
  "46252582109320"
], "DSIV-MCL-001 v1.0", "6ef5a1b754249bf9b552fd01594653f567d6aed84dae6fc8ab43d4589dad3234");
